import { useEffect, useRef, useState } from "react";
import { UploadCloud } from "lucide-react";
import api from "../lib/api";
import {
  listPendingRecordings, clearPendingRecording, uploadRecording,
} from "../lib/recording";
import { getBackgroundUpload, subscribeBackgroundUpload } from "../lib/backgroundUpload";

/**
 * Finishes any recording that never made it to the server — a dropped
 * connection, a closed tab, a proxy hiccup. Runs once per app load and shows a
 * small banner while it works, so a session recording is never silently lost.
 */
export default function PendingRecordingSync() {
  const [job, setJob] = useState(null); // { title, progress }
  const [bgJob, setBgJob] = useState(getBackgroundUpload()); // upload left running from the meeting screen
  const busy = useRef(false);
  const bgRef = useRef(getBackgroundUpload());

  useEffect(() => subscribeBackgroundUpload((s) => { bgRef.current = s; setBgJob(s); }), []);

  useEffect(() => {
    const run = async () => {
      if (busy.current) return;
      busy.current = true;
      try {
        const pending = await listPendingRecordings();
        for (const item of pending) {
          // Still being uploaded by the session that just ended — don't duplicate it.
          if (bgRef.current && bgRef.current.meetingId === item.meetingId) continue;
          // Older than 7 days, or the server already has it → drop it.
          if (Date.now() - (item.savedAt || 0) > 7 * 24 * 3600 * 1000) {
            await clearPendingRecording(item.meetingId);
            continue;
          }
          let meeting = null;
          try {
            meeting = (await api.get(`/meetings/${item.meetingId}`)).data;
          } catch {
            continue; // not ours / not reachable right now — try again later
          }
          if (!meeting || meeting.recording_path) {
            await clearPendingRecording(item.meetingId);
            continue;
          }
          setJob({ title: meeting.title || "session", progress: 0 });
          try {
            await uploadRecording(item.meetingId, item.blob, {
              mime: item.mime,
              ext: item.ext,
              onProgress: (p) => setJob((j) => (j ? { ...j, progress: p } : j)),
            });
            await clearPendingRecording(item.meetingId);
            api.post(`/meetings/${item.meetingId}/transcribe-and-minutes`).catch(() => {});
          } catch (e) {
            console.error("Pending recording upload failed — will retry later", e);
          }
          setJob(null);
        }
      } finally {
        busy.current = false;
      }
    };

    run();
    // Retry when the connection comes back, and periodically — a phone that
    // lost signal mid-upload shouldn't have to be reloaded.
    window.addEventListener("online", run);
    const timer = setInterval(run, 2 * 60 * 1000);
    return () => {
      window.removeEventListener("online", run);
      clearInterval(timer);
    };
  }, []);

  const active = job || bgJob;
  if (!active) return null;

  return (
    <div
      className="fixed left-3 right-3 bottom-3 sm:left-auto sm:right-4 sm:w-[340px] z-[70] rounded-lg border border-[#c89f45]/40 bg-[#121212] p-4 shadow-[0_20px_50px_rgba(0,0,0,0.6)]"
      data-testid="pending-recording-banner"
    >
      <div className="flex items-start gap-3">
        <UploadCloud size={16} className="text-[#c89f45] mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-mono uppercase tracking-widest text-[#c89f45]">Finishing an upload</p>
          <p className="text-sm text-[#ddd] mt-1 truncate">{active.title}</p>
          <div className="h-1 rounded bg-[#1c1c1c] overflow-hidden mt-3">
            <div className="h-full bg-[#c89f45]" style={{ width: `${Math.round(active.progress * 100)}%` }} />
          </div>
          <p className="text-[10px] font-mono text-[#8a8a8a] mt-2" data-testid="pending-recording-progress">
            {Math.round(active.progress * 100)}%
          </p>
        </div>
      </div>
    </div>
  );
}
